import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { User, Lock, Mail, Save, Shield, Building2, Calendar, Crown, Phone, Zap, Share2, Copy, DollarSign, Users } from 'lucide-react';
import AsaasConfig from '@/components/AsaasConfig';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/utils/api';

export default function Perfil() {
  const { user, company } = useAuth();
  
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    avatar_url: user?.avatar_url || '',
  });

  const [companyData, setCompanyData] = useState({
    name: company?.name || '',
    employee_count: company?.employee_count || '',
    average_revenue: company?.average_revenue || '',
  });

  const [is2FAEnabled, setIs2FAEnabled] = useState(user?.two_factor_enabled || false);
  const [loading2FA, setLoading2FA] = useState(false);

  React.useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        avatar_url: user.avatar_url || '',
      });
      setIs2FAEnabled(user.two_factor_enabled || false);
    }
    if (company) {
      setCompanyData({
        name: company.name || '',
        employee_count: company.employee_count || '',
        average_revenue: company.average_revenue || '',
      });
    }
  }, [user, company]);

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [loadingProfile, setLoadingProfile] = useState(false);
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [loadingCompany, setLoadingCompany] = useState(false);
  const [loadingAvatar, setLoadingAvatar] = useState(false);
  
  const fileInputRef = React.useRef(null);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoadingProfile(true);
    try {
      await api.put('/auth/profile', { 
        name: profileData.name, 
        phone: profileData.phone,
        avatar_url: profileData.avatar_url
      });
      toast.success('Perfil atualizado com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar perfil.');
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleCompanyUpdate = async (e) => {
    e.preventDefault();
    setLoadingCompany(true);
    try {
      await api.put('/company/profile', {
        name: companyData.name,
        employee_count: parseInt(companyData.employee_count) || null,
        average_revenue: parseFloat(companyData.average_revenue) || null
      });
      toast.success('Dados da agência atualizados!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar dados da agência.');
    } finally {
      setLoadingCompany(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoadingAvatar(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/user/upload-avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProfileData(prev => ({ ...prev, avatar_url: response.data.avatar_url }));
      toast.success('Foto de perfil atualizada!');
    } catch (error) {
      toast.error('Erro ao fazer upload da imagem.');
    } finally {
      setLoadingAvatar(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('A nova senha e a confirmação não coincidem.');
      return;
    }
    setLoadingPassword(true);
    try {
      await api.put('/auth/password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Sua senha foi alterada com segurança!');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar senha.');
    } finally {
      setLoadingPassword(false);
    }
  };

  const handleToggle2FA = async () => {
    setLoading2FA(true);
    try {
      const newState = !is2FAEnabled;
      await api.post('/auth/2fa/toggle', { enabled: newState });
      setIs2FAEnabled(newState);
      toast.success(newState ? 'A Verificação em 2 Etapas foi ativada!' : 'Verificação em 2 Etapas desativada.');
      // Importante: No caso real, chamaríamos refreshAuth(), usaremos user.two_factor_enabled como fallback
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alternar o 2FA.');
    } finally {
      setLoading2FA(false);
    }
  };

  const roleLabels = {
    vendedor: 'Vendedor CRM',
    desenvolvedor: 'Dev / Tech',
    operador: 'Operador',
    admin: 'Administrador',
    super_admin: 'Super Admin',
    gestor: 'Gestor',
  };

  const initials = (user?.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const referralLink = `${window.location.origin}/register?ref=${user?.referral_code}`;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado para a área de transferência!');
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header com Avatar */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-5">
          <div 
            className="group relative h-20 w-20 bg-gradient-to-br from-primary to-indigo-600 rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-lg shadow-primary/20 cursor-pointer overflow-hidden transition-transform hover:scale-105"
            onClick={() => fileInputRef.current?.click()}
          >
            {profileData.avatar_url ? (
               <img src={profileData.avatar_url} alt="Profile" className="h-full w-full object-cover" />
            ) : (
               initials
            )}
            
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
              <Zap className="h-5 w-5 text-white" />
            </div>
            
            {loadingAvatar && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
              </div>
            )}
          </div>
          
          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={handleAvatarUpload} 
            className="hidden" 
            accept="image/*"
          />

          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900">{user?.name || 'Usuário'}</h1>
            <p className="text-slate-500 text-sm">{user?.email}</p>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/20">
                <Crown className="h-3 w-3 mr-1" />
                {roleLabels[user?.role] || user?.role || 'Usuário'}
              </Badge>
              {companyData.name && (
                <Badge variant="outline" className="text-xs">
                  <Building2 className="h-3 w-3 mr-1" />
                  {companyData.name}
                </Badge>
              )}
              {company?.plan && (
                <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200">
                  Plano {String(company.plan).toUpperCase()}
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Info cards row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-slate-100">
          <div className="text-center">
            <p className="text-[11px] text-slate-400 uppercase tracking-wide">Cargo</p>
            <p className="text-sm font-semibold text-slate-700 mt-0.5">{roleLabels[user?.role] || 'N/A'}</p>
          </div>
          <div className="text-center">
            <p className="text-[11px] text-slate-400 uppercase tracking-wide">Empresa</p>
            <p className="text-sm font-semibold text-slate-700 mt-0.5">{company?.name || 'N/A'}</p>
          </div>
          <div className="text-center">
            <p className="text-[11px] text-slate-400 uppercase tracking-wide">Membro desde</p>
            <p className="text-sm font-semibold text-slate-700 mt-0.5">
              {user?.created_at ? new Date(user.created_at).toLocaleDateString('pt-BR') : 'N/A'}
            </p>
          </div>
          <div className="text-center">
            <p className="text-[11px] text-slate-400 uppercase tracking-wide">Último Acesso</p>
            <p className="text-sm font-semibold text-slate-700 mt-0.5">
              {user?.last_login ? new Date(user.last_login).toLocaleDateString('pt-BR') : 'Agora'}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
        {/* Card Informações Pessoais */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-full">
          <div className="p-5 border-b border-slate-100 flex items-center gap-3">
            <div className="h-9 w-9 bg-primary/10 rounded-lg flex items-center justify-center text-primary">
              <User className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-900">Informações Pessoais</h2>
              <p className="text-xs text-slate-500">Atualize seu nome e contato.</p>
            </div>
          </div>
          
          <form onSubmit={handleProfileUpdate} className="p-5 flex flex-col flex-1">
            <div className="space-y-4 flex-1">
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-xs">Nome Completo</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="name"
                    className="pl-10 h-9"
                    value={profileData.name}
                    onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                    placeholder="Seu nome completo"
                    required
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs">E-mail Cadastrado</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    className="pl-10 h-9 bg-slate-50"
                    value={profileData.email}
                    readOnly
                    disabled
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="phone" className="text-xs">Telefone / WhatsApp</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="phone"
                    type="tel"
                    className="pl-10 h-9"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                    placeholder="(11) 99999-9999"
                  />
                </div>
              </div>
            </div>
            
            <div className="pt-4 mt-4 border-t border-slate-50 flex justify-end">
              <Button type="submit" disabled={loadingProfile} size="sm">
                {loadingProfile ? 'Salvando...' : <><Save className="h-4 w-4 mr-1.5" /> Salvar</>}
              </Button>
            </div>
          </form>
        </div>

        {/* Card Dados da Agência — Só para Admins/Sócios */}
        {['admin', 'super_admin'].includes(user?.role) && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-full">
            <div className="p-5 border-b border-slate-100 flex items-center gap-3">
              <div className="h-9 w-9 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600">
                <Building2 className="h-4 w-4" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-slate-900">Dados da Agência</h2>
                <p className="text-xs text-slate-500">Informações estratégicas da sua empresa.</p>
              </div>
            </div>
            
            <form onSubmit={handleCompanyUpdate} className="p-5 flex flex-col flex-1">
              <div className="space-y-4 flex-1">
                <div className="space-y-1.5">
                  <Label htmlFor="companyName" className="text-xs">Nome da Empresa</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="companyName"
                      className="pl-10 h-9"
                      value={companyData.name}
                      onChange={(e) => setCompanyData({ ...companyData, name: e.target.value })}
                      placeholder="Nome da sua agência"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="employeeCount" className="text-xs">Funcionários</Label>
                    <div className="relative">
                      <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="employeeCount"
                        type="number"
                        className="pl-10 h-9"
                        value={companyData.employee_count}
                        onChange={(e) => setCompanyData({ ...companyData, employee_count: e.target.value })}
                        placeholder="Ex: 5"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="averageRevenue" className="text-xs">Faturamento Médio</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="averageRevenue"
                        type="number"
                        step="0.01"
                        className="pl-10 h-9"
                        value={companyData.average_revenue}
                        onChange={(e) => setCompanyData({ ...companyData, average_revenue: e.target.value })}
                        placeholder="R$ /mês"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="pt-4 mt-4 border-t border-slate-50 flex justify-end">
                <Button type="submit" disabled={loadingCompany} size="sm">
                  {loadingCompany ? 'Salvando...' : <><Save className="h-4 w-4 mr-1.5" /> Salvar Agência</>}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Card Segurança */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-full">
          <div className="p-5 border-b border-slate-100 flex items-center gap-3">
            <div className="h-9 w-9 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
              <Shield className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-900">Segurança</h2>
              <p className="text-xs text-slate-500">Altere sua senha de acesso.</p>
            </div>
          </div>
          
          <form onSubmit={handlePasswordUpdate} className="p-5 flex flex-col flex-1">
            <div className="space-y-4 flex-1">
              <div className="space-y-1.5">
                <Label htmlFor="current_password" className="text-xs">Senha Atual</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="current_password"
                    type="password"
                    className="pl-10 h-9"
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    placeholder="Sua senha atual"
                    required
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="new_password" className="text-xs">Nova Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="new_password"
                    type="password"
                    className="pl-10 h-9"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    placeholder="Mínimo de 6 caracteres"
                    minLength={6}
                    required
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="confirm_password" className="text-xs">Confirmar Nova Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="confirm_password"
                    type="password"
                    className="pl-10 h-9"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    placeholder="Repita a nova senha"
                    minLength={6}
                    required
                  />
                </div>
              </div>
            </div>
            
            <div className="pt-4 mt-4 border-t border-slate-50 flex justify-end">
              <Button type="submit" disabled={loadingPassword} size="sm">
                {loadingPassword ? 'Alterando...' : <><Save className="h-4 w-4 mr-1.5" /> Salvar Senha</>}
              </Button>
            </div>
          </form>
        </div>

        {/* Card Afiliados & Indicações */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-full">
          <div className="p-5 border-b border-slate-100 flex items-center gap-3">
            <div className="h-9 w-9 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600">
              <Share2 className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-900">Programa de Afiliados</h2>
              <p className="text-xs text-slate-500">Ganhe comissões indicando novos usuários.</p>
            </div>
          </div>
          
          <div className="p-5 space-y-6 flex-1 flex flex-col">
            <div className="space-y-6 flex-1">
              {/* Saldo e Métricas Rápidas */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-xl">
                  <div className="flex items-center gap-2 mb-1">
                    <DollarSign className="h-4 w-4 text-emerald-600" />
                    <span className="text-xs font-medium text-emerald-800">Saldo Disponível</span>
                  </div>
                  <p className="text-2xl font-bold text-emerald-700">
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(user?.referral_balance || 0)}
                  </p>
                  <p className="text-[10px] text-emerald-600 mt-1">Acumulado de comissões (10%)</p>
                </div>
                <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="h-4 w-4 text-blue-600" />
                    <span className="text-xs font-medium text-blue-800">Regra de Bônus</span>
                  </div>
                  <p className="text-lg font-bold text-blue-700">10% OFF</p>
                  <p className="text-[10px] text-blue-600 mt-1">Desconto aplicado na 1ª compra do seu indicado</p>
                </div>
              </div>

              {/* Código e Link */}
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-slate-700">Seu Cupom de Desconto</Label>
                  <div className="flex gap-2">
                    <div className="flex-1 px-3 h-9 bg-slate-50 border border-slate-200 rounded-lg flex items-center font-mono text-sm font-bold text-slate-600 uppercase tracking-widest">
                      {user?.referral_code || '---'}
                    </div>
                    <Button variant="outline" size="sm" className="h-9 px-3" onClick={() => copyToClipboard(user?.referral_code)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-slate-700">Link de Convite Direto</Label>
                  <div className="flex gap-2">
                    <div className="flex-1 px-3 h-9 bg-slate-50 border border-slate-200 rounded-lg flex items-center text-xs text-slate-500 truncate">
                      {referralLink}
                    </div>
                    <Button variant="outline" size="sm" className="h-9 px-3" onClick={() => copyToClipboard(referralLink)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Como funciona */}
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                <h4 className="text-xs font-bold text-slate-700 mb-2 uppercase tracking-tight">Como funciona o programa?</h4>
                <ul className="text-xs text-slate-600 space-y-2">
                  <li className="flex gap-2 items-start">
                    <span className="w-4 h-4 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] shrink-0 mt-0.5">1</span>
                    <span>Compartilhe seu link ou <b>Cupom de Desconto</b> com amigos e parceiros.</span>
                  </li>
                  <li className="flex gap-2 items-start">
                    <span className="w-4 h-4 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] shrink-0 mt-0.5">2</span>
                    <span>Quem usar seu cupom ganha <b>10% de desconto imediato</b> na primeira compra (mensal ou anual).</span>
                  </li>
                  <li className="flex gap-2 items-start">
                    <span className="w-4 h-4 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] shrink-0 mt-0.5">3</span>
                    <span>Você recebe <b>10% do valor pago</b> como crédito para abater em suas próximas faturas no Kode Studio.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Card 2FA - Full Width */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden p-6 flex flex-col md:flex-row items-center justify-center gap-6">
        <div className="flex-1 text-center md:text-left flex flex-col items-center md:items-start">
          <h3 className="text-lg font-semibold text-slate-900 flex flex-col md:flex-row items-center gap-3">
            <span className="flex items-center justify-center w-10 h-10 rounded-full bg-red-50 text-red-500">
              <Shield className="h-5 w-5" />
            </span>
            Autenticação de 2 Fatores
            {is2FAEnabled ? (
               <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs py-0.5">Ativado</Badge>
            ) : (
               <Badge variant="outline" className="bg-slate-100 text-slate-500 border-slate-200 text-xs py-0.5">Desativado</Badge>
            )}
          </h3>
          <p className="text-sm text-slate-500 mt-2 max-w-lg">
            Adiciona uma camada extra de proteção à sua conta Kode. Exigiremos um código de 6 dígitos enviado ao seu e-mail padrão a cada novo login.
          </p>
        </div>
        <Button
          type="button"
          onClick={handleToggle2FA}
          disabled={loading2FA}
          className={
            is2FAEnabled 
              ? "bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 shadow-sm min-w-[220px] h-12 text-sm font-semibold" 
              : "bg-red-500 hover:bg-red-600 text-white shadow-sm min-w-[220px] h-12 text-sm font-semibold"
          }
        >
          {loading2FA ? 'Processando...' : is2FAEnabled ? 'Desativar Autenticação' : 'Ativar Autenticação de 2 fatores'}
        </Button>
      </div>

      {/* Integrações — só para admin/super_admin */}
      {['admin', 'super_admin'].includes(user?.role) && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-slate-100 flex items-center gap-3">
            <div className="h-9 w-9 bg-violet-100 rounded-lg flex items-center justify-center text-violet-600">
              <Zap className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-900">Integrações</h2>
              <p className="text-xs text-slate-500">Conecte ferramentas externas à sua conta.</p>
            </div>
          </div>
          <div className="p-5">
            <AsaasConfig />
          </div>
        </div>
      )}
    </div>
  );
}
